import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { environment } from 'environment';
import { DonateNet } from 'networking';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import 'react-loading-skeleton/dist/skeleton.css';
import { classes, priceWithFee } from 'shared';
import { useAlerts, useUser } from 'state';
import {
  defaultDonationCampaigns,
  Form,
  FormErrors,
  getError,
  handleForm,
  monetaryDonationSchemas,
  zHelpers,
  type ZInfer,
} from 'types';
import { EmailInput } from '../components/form/email';
import { Input } from '../components/form/input';
import { type AmountDescriptionMode } from '../components/form/monetary-donations/donate-amount';
import { Button } from '../components/general/button';
import { LogoMainLong } from '../components/img/logo';
import { Meta } from '../components/meta/meta';
// import { umami } from '../../../scripts/tracking';

export default function DeliveryFees() {
  return (
    <>
      <Meta title="Delivery Fees" description="Make a Beds for Kids Delivery Fee Payment" />

      <main className="!bg-grey-50 xl:py-16">
        <div className="relative z-40 mx-4 mb-12 flex max-w-lg flex-col items-center justify-center pt-12 sm:mx-6 md:mx-auto">
          <LogoMainLong className="block min-h-[200px] w-auto max-w-full" />
        </div>
        <DeliveryFeeForm fixedCampaign={defaultDonationCampaigns.deliveryFee} />
      </main>
    </>
  );
}

export function DeliveryFeeForm({
  fixedCampaign,
  embed,
  descriptionMode,
}: {
  fixedCampaign?: { id: string; name: string };
  embed?: boolean;
  descriptionMode?: AmountDescriptionMode;
}) {
  return (
    <>
      <Script src="https://js.stripe.com/v3" async defer />
      <GoogleReCaptchaProvider
        reCaptchaKey={environment.google.recaptchaKey}
        scriptProps={{ async: true, defer: true }}
        useEnterprise>
        <DeliveryFeeFormInner fixedCampaign={fixedCampaign} embed={embed} descriptionMode={descriptionMode} />
      </GoogleReCaptchaProvider>
    </>
  );
}
type LoadingState = { email: boolean; setup: boolean; cancelling: boolean; submitting: boolean };
export function DeliveryFeeFormInner({
  fixedCampaign,
  embed,
  descriptionMode = 'default',
}: {
  fixedCampaign?: { id: string; name: string };
  embed?: boolean;
  descriptionMode?: AmountDescriptionMode;
}) {
  const [form, setForm] = useState<Form<typeof monetaryDonationSchemas.request>>({
    campaign: fixedCampaign?.id || defaultDonationCampaigns.general.id,
    amount: 60,
    campaignMeta: descriptionMode,
    willPayFees: true,
  });
  const [errors, setErrors] = useState<FormErrors<typeof monetaryDonationSchemas.request>>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const router = useRouter();
  const { user } = useUser();
  const { amount: defaultAmount, interval, message } = router.query;
  const recurring = interval === 'recurring';

  const [loading, setLoading] = useState<LoadingState>({
    email: false,
    setup: false,
    cancelling: false,
    submitting: false,
  });

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    const { fname, lname, email, phone } = user;
    setForm({ ...form, fname, lname, email, phone, exists: true });
  }, [user]);

  useEffect(() => {
    if (!defaultAmount) return;
    setForm({ ...form, amount: Number(defaultAmount) });
  }, [defaultAmount]);

  useEffect(() => {
    if (!message) return;
    setForm(f => ({ ...f, message: String(message) }));
  }, [message]);

  useEffect(() => {
    if (!fixedCampaign) return;
    setForm(f => ({ ...f, campaign: fixedCampaign.id }));
  }, [fixedCampaign]);

  return (
    <>
      <main className={classes('container mx-auto flex max-w-3xl flex-col', !embed && 'mb-12 px-4')}>
        <DeliveryFeeAmount
          disabled={loading.setup || step > 0}
          amount={form.amount}
          updateAmount={v => handleForm(setForm, setErrors, errors, 'amount', v)}
          eAmount={getError(errors, 'amount')}
          updateEAmount={v => setErrors(e => ({ ...e, amount: { _errors: [v] } }))}
        />
        {step === 0 && (
          <>
            <section className="mt-8 rounded-md shadow">
              <div className="flex flex-row flex-wrap gap-y-4 rounded-md bg-white px-4 py-5 sm:p-6">
                <EmailInput
                  className="w-full"
                  name="Email Address"
                  autoComplete="email"
                  email={form.email}
                  onUpdate={v => handleForm(setForm, setErrors, errors, 'email', v)}
                  error={getError(errors, 'email')}
                  onUpdateError={v => setErrors(e => ({ ...e, email: { _errors: [v] } }))}
                  spinner={loading.email}
                />
                <Input
                  className="w-full"
                  name="Who is this delivery fee for?"
                  placeholder="Myself/[Recipient Name]"
                  noHideWithPlaceholder
                  fKey="message"
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  setErrors={setErrors}
                />
              </div>
            </section>
            <section className="flex flex-col items-center p-4">
              <p className="mb-2 text-sm text-red-600">{errorMessage}</p>
              <Button size="xl" loadingText="Loading..." loading={loading.setup} onClick={setup}>
                Continue
              </Button>
            </section>
          </>
        )}
        {step === 1 && (
          <>
            <Elements
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              stripe={loadStripe(environment.stripe.apiKey) as unknown as Stripe | PromiseLike<Stripe>}
              options={{
                mode: 'payment',
                amount: Math.round(form.amount * 100),
                currency: 'usd',
                paymentMethodCreation: 'manual',
              }}>
              <Payment
                form={form}
                setForm={setForm}
                errors={errors}
                setErrors={setErrors}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                loading={loading}
                setLoading={setLoading}
                recurring={recurring}
              />
            </Elements>
          </>
        )}
      </main>
    </>
  );

  function setup() {
    if (!zHelpers.email.safeParse(form.email).success)
      setErrors(e => ({ ...e, email: { _errors: ['Invalid email'] } }));
    if (form.amount < 5)
      setErrors(e => ({ ...e, amount: { _errors: ['Minimum donation is $5.00 to prevent fraud - sorry'] } }));
    if (form.campaign === defaultDonationCampaigns.deliveryFee.id && !form.message)
      setErrors(e => ({ ...e, message: { _errors: ['Required'] } }));
    if (loading.setup || !zHelpers.email.safeParse(form.email).success || !form.message || form.amount < 5) return;
    setErrorMessage(null);
    setErrors(null);
    return setStep(1);
  }
}

function Payment({
  form,
  setForm,
  errors,
  setErrors,
  errorMessage,
  setErrorMessage,
  loading,
  setLoading,
  recurring,
}: {
  form: Form<typeof monetaryDonationSchemas.request>;
  errors: FormErrors<typeof monetaryDonationSchemas.request>;
  setForm: Dispatch<SetStateAction<Form<typeof monetaryDonationSchemas.request>>>;
  setErrors: Dispatch<SetStateAction<FormErrors<typeof monetaryDonationSchemas.request>>>;
  errorMessage: string | null;
  setErrorMessage: (e: string | null) => void;
  loading: LoadingState;
  setLoading: Dispatch<SetStateAction<LoadingState>>;
  recurring: boolean;
}) {
  const [recaptchaV2, setRecaptchaV2] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const stripe = useStripe();
  const elements = useElements();
  const { addAlert } = useAlerts();

  return (
    <>
      <section className="mt-8 rounded-md shadow">
        <div className="flex flex-row flex-wrap gap-y-4 rounded-md bg-white px-4 py-5 sm:p-6">
          <AddressElement
            options={{
              mode: 'billing',
              allowedCountries: ['US'],
              fields: { phone: 'always' },
              display: { name: 'split' },
              validation: { phone: { required: 'always' } },
            }}
            className="w-full"
            onChange={handleAddressElementUpdate}
          />
        </div>
      </section>
      <section className="mt-8 rounded-md shadow">
        <div className="flex flex-row flex-wrap gap-y-4 rounded-md bg-white px-4 py-5 sm:p-6">
          <PaymentElement
            onChange={e =>
              e.value.type !== form.method && handleForm(setForm, setErrors, errors, 'method', e.value.type)
            }
            className="w-full"
          />
        </div>
      </section>
      <section className="mt-1">
        <p className="text-center text-xs text-gray-400">
          Secure Payment &bull; This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" className="text-blue-500 hover:underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" className="text-blue-500 hover:underline">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </section>
      {form.campaign !== defaultDonationCampaigns.deliveryFee.id && (
        <section className="px-4 py-5 sm:p-6">
          <div className="relative flex items-start md:justify-center">
            <div className="flex h-5 items-center">
              <input
                id="comments"
                aria-describedby="comments-description"
                name="comments"
                type="checkbox"
                checked={form.willPayFees}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 hover:cursor-pointer focus:ring-blue-500"
                onChange={v => handleForm(setForm, setErrors, errors, 'willPayFees', v.target.checked)}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="comments" className="font-medium text-gray-700 hover:cursor-pointer">
                Fees
              </label>
              <p id="comments-description" className="text-gray-500">
                I will generously cover the{' '}
                {form.method === 'us_bank_account' ? (
                  <>$5.00 ACH wiring fee. (${((form.amount || 0) + 5).toFixed(2)})</>
                ) : (
                  <>
                    2.2% + 30&#162; credit card fee. ($
                    {priceWithFee(form.amount || 0).toFixed(2)})
                  </>
                )}
              </p>
            </div>
          </div>
        </section>
      )}
      <div id="captcha-v2" className="mx-auto" />
      <p className="mx-auto mt-4 max-w-xl text-center text-sm text-red-600 empty:hidden">{errorMessage}</p>
      <div className="flex flex-wrap justify-center gap-4 pt-4">
        <Button
          type="submit"
          onClick={submit}
          loading={loading.submitting}
          disabled={loading.cancelling}
          loadingText="Submitting...">
          Submit
        </Button>
      </div>
    </>
  );

  function handleAddressElementUpdate(e: StripeAddressElementChangeEvent) {
    const { value } = e;

    setForm(f => ({
      ...f,
      fname: value.firstName,
      lname: value.lastName,
      phone: value.phone,
      address: {
        ...f?.address,
        street: value.address.line1,
        street2: value.address.line2,
        city: value.address.city,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
        state: value.address.state as any,
        zip: value.address.postal_code,
      },
    }));
  }

  async function submit() {
    if (loading.submitting) return;
    setErrorMessage(null);
    setLoading(l => ({ ...l, submitting: true }));

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage('Please fill out all required fields');
      setLoading(l => ({ ...l, submitting: false }));
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: { billing_details: { email: form.email } },
    });

    if (error) {
      setErrorMessage(error.message);
      setLoading(l => ({ ...l, submitting: false }));
      return;
    }

    let v2Token: string | undefined;
    if (recaptchaV2) {
      v2Token = window.grecaptcha.getResponse();
      if (!v2Token) {
        setErrorMessage('please click the checkbox to help us verify you are not a robot');
        setLoading(l => ({ ...l, submitting: false }));
        return;
      }
    }

    const token = await executeRecaptcha('donation');
    const data = {
      ...form,
      amount: form.amount * 100,
      subscription: recurring,
      recaptcha: { token, action: 'donation' },
      paymentMethod: paymentMethod.id,
      recaptchaV2: recaptchaV2 ? { token: v2Token } : undefined,
    };
    const valid = await monetaryDonationSchemas.request.spa(data);
    console.log('data', data);
    console.log('valid', valid);
    if (valid.success === false) return setErrorMessage(valid.error.message);

    const res = await DonateNet.complete(valid.data);

    if (res.success === false) {
      switch (res.error.message) {
        case 'bottington':
          addAlert({
            title: 'Turing Test Failed',
            body: "Bots are red, CAPTCHAs are blue, we think you're a bot, but we hope it's not true! Let's give this another shot. We hope you like clicking pictures!",
            icon: 'error',
            important: true,
          });
          setErrorMessage('please click the checkbox to help us verify you are not a robot');
          if (typeof window === 'undefined' || !window.grecaptcha?.render) break;
          setRecaptchaV2(true);
          window.grecaptcha.render('captcha-v2', { sitekey: environment.google.recaptchaV2Key, size: 'normal' });
          break;
        case 'superbottington':
          addAlert({
            title: 'Are You Sure You Are Not A Robot?',
            body: "We're sorry, but we think you're a bot. Please try again with a different device or browser.",
            icon: 'error',
            important: true,
          });
          setErrorMessage('please use a different browser or device to donate');
          break;
      }

      setLoading(l => ({ ...l, submitting: false }));
      return;
    }

    if (valid.data.subscription) await recurringDonation(res.data.secret);
    else await oneTimeDonation(valid.data, res.data.secret);
  }

  async function recurringDonation(clientSecret: string) {
    const { error } = await stripe.confirmPayment({
      clientSecret,
      elements,
      confirmParams: { return_url: `${environment.urls.api}/donate/stripe/subscription/callback` },
    });

    if (error) {
      setLoading(l => ({ ...l, submitting: false }));
      setErrorMessage(error.message);
    }
  }

  async function oneTimeDonation(data: ZInfer<typeof monetaryDonationSchemas.request>, clientSecret: string) {
    const { error } = await stripe.confirmPayment({
      clientSecret,
      elements,
      confirmParams: { return_url: `${environment.urls.api}/donate/stripe/callback/${data.campaign}` },
    });

    if (error) {
      setLoading(l => ({ ...l, submitting: false }));
      setErrorMessage(error.message);
    }
  }
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function DeliveryFeeTypeSelect({
  label = 'What type of delivery fee are you paying?',
  value,
  onChange,
  error: _error,
  onChangeError,
  disabled,
  cClassName,
  lClassName,
  overRideClassName,
  noLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  onChangeError?: (error: string | undefined) => void;
  disabled?: boolean;
  label?: string;
  cClassName?: string;
  lClassName?: string;
  overRideClassName?: boolean;
  noLabel?: boolean;
}) {
  const options = [
    { id: 'bedsOnly', name: 'Beds only' },
    { id: 'bedsAndFurniture', name: 'Beds and other furniture' },
  ];

  const selected = { id: value, name: options.find(o => o.id === value)?.name || '' };

  return (
    <Listbox value={selected} onChange={updateSelection} disabled={disabled}>
      {({ open }) => (
        <>
          <div className={calculateCClassName()}>
            {!noLabel && (
              <label
                className={classes(
                  'absolute -top-2 left-2 z-[1] -mt-px inline-flex bg-white px-1 text-xs font-medium text-gray-900',
                  (cClassName?.includes('md:pl-2') || cClassName?.includes('md:px-2')) && 'md:ml-2',
                  disabled && 'bg-gradient-to-b from-white via-white to-gray-200',
                  lClassName,
                )}>
                {label}
              </label>
            )}
            <Listbox.Button className="relative block w-full cursor-pointer rounded-md border border-gray-300 bg-white px-4 py-3 pl-3 pr-10 text-left placeholder-gray-500 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600">
              <span className="block truncate">{selected.name}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {options.map(c => (
                  <Listbox.Option
                    key={c.id}
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-blue-600 text-white' : 'text-gray-900',
                        'relative cursor-pointer select-none py-2 pl-8 pr-4',
                      )
                    }
                    value={c}>
                    {({ selected, active }) => (
                      <>
                        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                          {c.name}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? 'text-white' : 'text-blue-600',
                              'absolute inset-y-0 left-0 flex items-center pl-1.5',
                            )}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );

  function calculateCClassName() {
    if (overRideClassName) return cClassName;
    return classes('relative mt-[8px] max-w-full', cClassName);
  }

  function updateSelection(selection: (typeof options)[0]) {
    if (selection.id === null) onChangeError('Please select a delivery type');
    onChange(selection.id);
  }
}

export function DeliveryFeeAmount({
  amount,
  updateAmount,
  eAmount,
  updateEAmount,
  disabled,
}: {
  amount: number | undefined;
  updateAmount: (v: number) => void;
  eAmount: string | string[] | undefined;
  updateEAmount: (v: string | undefined) => void;
  disabled: boolean;
}) {
  const [dispAmount, setDispAmount] = useState(`$${amount?.toFixed(2) || '0.00'}`);
  const [deliveryFeeType, setDeliveryFeeType] = useState<string>('bedsOnly');
  const {
    query: { amount: amt },
  } = useRouter();
  useEffect(() => amt && finalizeAmount(Number(amt)), [amt]);

  return (
    <section className="rounded-md shadow">
      <div className="flex flex-col rounded-md bg-white px-4 py-5 sm:p-6">
        <DeliveryFeeTypeSelect
          value={deliveryFeeType}
          onChange={v => {
            setDeliveryFeeType(v);
            if (v === 'bedsOnly') {
              finalizeAmount(60);
            } else if (v === 'bedsAndFurniture') {
              finalizeAmount(100);
            }
          }}
          disabled={disabled}
        />
        <div className={classes('flex items-center border-gray-300', 'pt-4')}>
          <div className={`w-full px-1`}>
            <Input
              name="Amount"
              value={dispAmount}
              error={eAmount}
              onUpdate={handleAmount}
              onBlur={() => finalizeAmount(amount)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </section>
  );

  function finalizeAmount(amt?: number, local?: boolean) {
    const workingAmt = amt ? String(amt) : dispAmount;
    if (!workingAmt) {
      if (!local) updateAmount(0);
      return;
    }
    const s = Number(workingAmt.replace(/[^.\d]/g, ''));
    if (!local) updateAmount(s);
    setDispAmount(`$${s.toFixed(2)}`);
    if (eAmount) {
      updateEAmount('');
    }
  }

  function handleAmount(v: string) {
    if (!v) {
      updateAmount(0);
      setDispAmount(``);
      return;
    }
    updateAmount(Number(v.replace(/[^.\d]/g, '')));
    const s = v.replace(/[^.\d]/g, '');
    setDispAmount(`$${s}`);
    if (eAmount) {
      updateEAmount('');
    }
  }
}
